import { useState, useEffect } from "react";
import { Table } from "antd";
import ChantPopConfirm from "../../../CommonComponents/ChantTooltip/ChantPopConfirm";
import moment from "moment";
import {
  deletePolls,
  fbGetPolls,
  postLive as postLiveApi,
} from "../../../services/firebaseService/endPoints/poll";
import { withRouter } from "../../../utils/helper";
import useGroupsData from "../group/useGroupsData";
import { ChantButton, ScreenHeader } from "../../../CommonComponents";
import SuperAdminContainer from "../SuperAdminContainer";

const SuperAdminPolls = ({ router }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const { groups } = useGroupsData();

  useEffect(() => {
    fbGetPolls().then((polls) => {
      setIsLoading(false);
      setTableData(polls);
    });
  }, []);

  const handlePostLive = (id) => {
    setIsLoading(true);
    postLiveApi(id).then(() => {
      fbGetPolls().then((polls) => {
        setTableData(polls);
        setIsLoading(false);
      });
    });
  };

  const handleEdit = (row) => {
    router.navigate(`/superadmin/polls/edit/${row.id}`);
  };

  const handleDelete = (row) => {
    setIsLoading(true);
    deletePolls(row.id).then(() => {
      fbGetPolls().then((polls) => {
        setTableData(polls);
        setIsLoading(false);
      });
    });
  };

  const columns = [
    {
      title: "Action",
      width: window.screen.width > 500 ? 150 : 100,
      key: "action",
      className: "header-white",
      render: (row) => {
        const currentTime = moment().unix();
        return (
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "space-around",
              alignItems: "center",
            }}
          >
            {row.isActive && currentTime <= row.closesOn ? (
              <span style={{ color: "red" }}>Live</span>
            ) : currentTime > row.closesOn ? (
              <span>Closed</span>
            ) : (
              <ChantPopConfirm
                title="Are you sure?"
                okText="Yes"
                cancelText="No"
                onConfirm={() => handlePostLive(row.id)}
                onCancel={() => {}}
              >
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger mt-3 mb-2"
                >
                  Post Live
                </button>
              </ChantPopConfirm>
            )}
            {(!row.isActive || currentTime <= row.closesOn) && (
              <>
                <i
                  className="fa fa-pencil cursor-pointer"
                  onClick={() => handleEdit(row)}
                />
                <ChantPopConfirm
                  title="Are you sure?"
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => handleDelete(row)}
                  onCancel={() => {}}
                >
                  <i className="fa fa-trash cursor-pointer" />
                </ChantPopConfirm>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: "Date Posted",
      key: "postedOn",
      dataIndex: "postedOn",
      className: "header-white",
      render: (data) => (data ? moment.unix(data).format("MM/DD/YY") : ""),
    },
    {
      title: "Time Remaining",
      key: "timeRemaining",
      dataIndex: "closesOn",
      className: "header-white",
      render: (data) => {
        let timeRemaining = data - moment().unix();
        if (!data || timeRemaining < 0) return "";
        if (timeRemaining < 60) return `${timeRemaining} sec`;
        if (timeRemaining < 60 * 60) return `${Math.round(timeRemaining / 60.0)} minutes`;
        if (timeRemaining < 24 * 60 * 60) return `${Math.round(timeRemaining / (60.0 * 60.0))} hours`;
        return `${Math.round(timeRemaining / (24 * 60 * 60))} days`;
      },
    },
    {
      title: "Groups",
      key: "groups",
      className: "header-white",
      dataIndex: "groups",
      render: (groupIds) => {
        let targets = [];
        groupIds.forEach((param) => {
          let group = groups.find((g) => g?.id === param);
          if (group) targets.push(group?.groupName);
          let groupForLeague = groups.find((g) => g?.leagueId === param);
          if (groupForLeague) targets.push(group?.leagueName);
          let groupForClub = groups.find((g) => g?.clubId === param);
          if (groupForClub) targets.push(groupForClub.clubName);
        });
        targets = Array.from(new Set(targets));
        return (
          <ul style={{ listStyle: "none" }}>
            {targets.map((target) => (
              <li key={target}>{target}</li>
            ))}
          </ul>
        );
      },
    },
    {
      title: "Question",
      key: "question",
      className: "header-white",
      dataIndex: "desc",
      render: (data) => data || "",
    },
    {
      title: "Responses(%)",
      key: "response",
      className: "header-white",
      render: (data) => {
        const currentTime = moment().unix();
        if (data.isActive || currentTime > data.closesOn) {
          return (
            <ul style={{ listStyle: "none" }}>
              {Object.entries(data.stats).map(([key, value]) => (
                <li key={key}>{`${key}   ${value}%`}</li>
              ))}
            </ul>
          );
        }
        return (
          <ul style={{ listStyle: "none" }}>
            {Array.from({ length: 5 }, (_, index) => index)
              .filter((index) => data[`op${index + 1}`] && data[`op${index + 1}`] !== null)
              .map((index) => (
                <li key={index}>{data[`op${index + 1}`].name}</li>
              ))}
          </ul>
        );
      },
    },
    {
      title: "#responses",
      key: "entries",
      className: "header-white",
      dataIndex: "noOfPolls",
      render: (entries) => entries || 0,
    },
    {
      title: "Response Rate(%)",
      key: "resRate",
      className: "header-white",
      render: (data) => {
        if (data.targetMembersCount === 0) return 0;
        return (data.noOfPolls * 100) / data.targetMembersCount;
      },
    },
    {
      title: "Sponsor",
      key: "sponsorName",
      className: "header-white",
      dataIndex: "sponsorName",
      render: (name) => name || "",
    },
  ];

  return (
    <SuperAdminContainer>
      <ScreenHeader
        title="Polls"
        elementCount={tableData.length}
        actions={
          <ChantButton onClick={() => router.navigate("/superadmin/polls/create")}>
            Create New
          </ChantButton>
        }
      />
      <Table loading={isLoading} columns={columns} dataSource={tableData} bordered />
    </SuperAdminContainer>
  );
};

export default withRouter(SuperAdminPolls);
